import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Bell, MonitorSmartphone, Receipt, Wallet, Download, RefreshCw, LogOut,
  ShieldCheck, CalendarPlus,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { EmptyState, CardSkeleton } from './common/States';
import {
  notificationPreferenceAPI, sessionAPI, itStatementAPI, hrOpsAPI,
  holidayCalendarAPI,
} from '../services/api';
import { getErrorMessage } from '../lib/utils';

const TOPIC_LABELS = {
  leave_decision: 'Leave approved or rejected',
  attendance_reminder: 'Attendance reminders',
  approval_request: 'Something needs your approval',
  payslip_released: 'Payslip released',
  announcement: 'Company announcements',
  helpdesk_update: 'Helpdesk ticket updates',
  policy_acknowledgement: 'Policies to acknowledge',
};

const CHANNEL_LABELS = { in_app: 'In app', email: 'Email', push: 'Push' };

const DIGEST = [
  { value: 'instant', label: 'As it happens' },
  { value: 'daily', label: 'Daily digest' },
  { value: 'weekly', label: 'Weekly digest' },
];

const money = (v) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(Number(v || 0));

const titleize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const Section = ({ icon: Icon, title, description, action, loading, isEmpty, emptyText, children }) => (
  <Card>
    <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
      <div className="space-y-1">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </CardHeader>
    <CardContent>
      {loading && <CardSkeleton rows={4} />}
      {!loading && isEmpty && <EmptyState compact title={emptyText} />}
      {!loading && !isEmpty && children}
    </CardContent>
  </Card>
);

/** Saves the blob the browser was handed, without leaking the object URL. */
const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * The account surfaces an employee owns outright: what reaches them, where
 * they are signed in, what they earned, and what they owe.
 */
const MyAccountModule = ({ currentTab = 'notifications' }) => {
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState(null);
  const [sessions, setSessions] = useState({ policy: null, items: [] });
  const [statement, setStatement] = useState(null);
  const [loans, setLoans] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      notificationPreferenceAPI.get(),
      sessionAPI.listMine(),
      itStatementAPI.get(),
      hrOpsAPI.listLoans(),
    ]);
    const value = (r, fallback) => (r.status === 'fulfilled' ? r.value.data : fallback);
    setPrefs(value(results[0], null));
    setSessions(value(results[1], { policy: null, items: [] }));
    setStatement(value(results[2], null));
    setLoans(value(results[3], []));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePrefs = async (patch) => {
    setSaving(true);
    try {
      const { data } = await notificationPreferenceAPI.update(patch);
      setPrefs(data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not save preferences'));
    } finally {
      setSaving(false);
    }
  };

  const toggleChannel = (topic, channel, enabled) =>
    savePrefs({ channels: { [topic]: { [channel]: enabled } } });

  const revoke = async (session) => {
    try {
      await sessionAPI.revoke(session.session_id);
      toast.success(session.is_current ? 'Signed out here' : 'Device signed out');
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not sign that device out'));
    }
  };

  const downloadIcs = async () => {
    try {
      const year = new Date().getFullYear();
      const { data } = await holidayCalendarAPI.downloadIcs(year);
      saveBlob(data, `holidays-${year}.ics`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not build the calendar file'));
    }
  };

  if (currentTab === 'notifications') {
    const channels = prefs?.channels || {};
    const available = prefs?.available_channels || [];
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={Bell}
          title="Notifications"
          description="Choose what reaches you, and how."
          action={<Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>}
          loading={loading}
          isEmpty={!prefs}
          emptyText="Preferences unavailable."
        >
          {prefs && (
            <div className="space-y-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Notify me about</TableHead>
                    {available.map((c) => (
                      <TableHead key={c} className="text-center w-24">
                        {CHANNEL_LABELS[c] || titleize(c)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(prefs.topics || []).map((topic) => (
                    <TableRow key={topic}>
                      <TableCell className="font-medium">
                        {TOPIC_LABELS[topic] || titleize(topic)}
                      </TableCell>
                      {available.map((c) => (
                        <TableCell key={c} className="text-center">
                          <Switch
                            checked={!!channels[topic]?.[c]}
                            disabled={saving}
                            onCheckedChange={(v) => toggleChannel(topic, c, v)}
                            aria-label={`${TOPIC_LABELS[topic] || topic} via ${CHANNEL_LABELS[c] || c}`}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label>Delivery</Label>
                  <Select
                    value={prefs.digest_frequency || 'instant'}
                    onValueChange={(v) => savePrefs({ digest_frequency: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DIGEST.map((d) => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quiet hours from</Label>
                  <Input
                    type="time"
                    value={prefs.quiet_hours_start || ''}
                    onChange={(e) => savePrefs({ quiet_hours_start: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Quiet hours until</Label>
                  <Input
                    type="time"
                    value={prefs.quiet_hours_end || ''}
                    onChange={(e) => savePrefs({ quiet_hours_end: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}
        </Section>

        <Section
          icon={CalendarPlus}
          title="Holiday calendar"
          description="Add this year's holidays to Google, Outlook or Apple Calendar."
          loading={false}
          isEmpty={false}
        >
          <Button variant="outline" onClick={downloadIcs}>
            <Download className="h-4 w-4 mr-1" /> Download .ics
          </Button>
        </Section>
      </div>
    );
  }

  if (currentTab === 'devices') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={MonitorSmartphone}
          title="Signed-in devices"
          description="Anything you do not recognise, sign it out."
          action={<Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>}
          loading={loading}
          isEmpty={!(sessions.items || []).length}
          emptyText="No active sessions."
        >
          <div className="space-y-4">
            {sessions.policy?.enforced && (
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 p-3 text-sm">
                <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">
                  Your workspace allows {sessions.policy.max_active_sessions} active
                  {sessions.policy.max_active_sessions === 1 ? ' session' : ' sessions'} at a time.
                </span>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sessions.items || []).map((s) => (
                  <TableRow key={s.session_id}>
                    <TableCell>
                      <span className="font-medium">{s.device_label}</span>
                      {s.is_current && <Badge className="ml-2">This device</Badge>}
                      {!s.is_live && <Badge variant="outline" className="ml-2">Signed out</Badge>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{titleize(s.client)}</TableCell>
                    <TableCell className="text-muted-foreground">{s.ip_address || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {String(s.last_seen_at || '').replace('T', ' ').slice(0, 16) || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {s.is_live && (
                        <Button size="sm" variant="outline" onClick={() => revoke(s)}>
                          <LogOut className="h-4 w-4 mr-1" />
                          {s.is_current ? 'Sign out' : 'Revoke'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Section>
      </div>
    );
  }

  if (currentTab === 'tax-statement') {
    const totals = statement?.totals || {};
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={Receipt}
          title={`Tax statement ${statement?.financial_year || ''}`}
          description={statement?.note}
          action={<Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>}
          loading={loading}
          isEmpty={!statement || !statement.payslip_count}
          emptyText="No payslips issued in this financial year yet."
        >
          {statement && (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  ['Gross earnings', totals.gross_earnings],
                  ['Total deductions', totals.total_deductions],
                  ['TDS', totals.tds],
                  ['Net paid', totals.net_salary],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-border p-4">
                    <div className="text-xs text-muted-foreground">{label}</div>
                    <div className="text-2xl font-semibold tabular-nums">{money(value)}</div>
                  </div>
                ))}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Component</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    ['Provident fund (employee)', totals.pf_employee],
                    ['ESI (employee)', totals.esi_employee],
                    ['Professional tax', totals.professional_tax],
                    ['Other deductions', totals.other_deductions],
                  ].map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell>{label}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-muted-foreground">
                Compiled from {statement.payslip_count} payslip
                {statement.payslip_count === 1 ? '' : 's'} covering{' '}
                {statement.period?.from} to {statement.period?.to}.
              </p>
            </div>
          )}
        </Section>
      </div>
    );
  }

  // loans
  return (
    <div className="space-y-6 animate-fade-in">
      <Section
        icon={Wallet}
        title="Loans & advances"
        description="What you have borrowed and what is left to repay."
        action={<Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>}
        loading={loading}
        isEmpty={!loans.length}
        emptyText="No loans or advances on record."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Principal</TableHead>
              <TableHead className="text-right">Instalment</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loans.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="font-medium">{titleize(l.loan_type)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(l.principal)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(l.instalment_amount)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(l.outstanding)}
                </TableCell>
                <TableCell>
                  <Badge variant={l.status === 'closed' ? 'outline' : 'secondary'}>
                    {titleize(l.status)}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </div>
  );
};

export default MyAccountModule;
